import { SubscriptionsContext } from "@/context/SubscriptionsContext";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useRef, useState } from "react";

// SubscriptionsProvider used to fetch GET /Subscription once on mount, so the UI
// showed stale subscription flags after any package-apply or flag change until a
// hard reload. We now also re-fetch when the window/tab regains focus (debounced
// to avoid hammering the endpoint on rapid focus events).
const FOCUS_REFETCH_DEBOUNCE_MS = 30000;

// This interface must stay in sync with messaging app's SubscriptionsProvider.
// Both apps call the same GET /Subscription API endpoint.
export interface Subscription {
  id: number;
  siteId: number;
  // Channels
  enableWeb: boolean;
  enableSms: boolean;
  enableEmail: boolean;
  enableWhatsapp: boolean;
  enableFacebook: boolean;
  enableInstagram?: boolean;
  enableTikTok?: boolean;
  enableIvr: boolean;
  // Core Features
  enableNetsuite: boolean;
  enableAutomation: boolean;
  enableAI: boolean;
  enableWorkflowRules: boolean;
  enableFormstack: boolean;
  enableWebflow: boolean;
  enableContactManager: boolean;
  enableKnowledgeBase: boolean;
  enableInvitations: boolean;
  enableSemanticSearch: boolean;
  enableAdvancedAIFeatures: boolean;
  /** "basic" | "pro" | "enterprise" — derived server-side from DefaultAiModel, not stored. */
  aiModelTier?: string;
  /** Site-level default AI model alias set by superadmin. Null = platform default. */
  defaultAiModel?: string;
  /** True when Velaro has configured a custom API key for this site. Key itself is never exposed. */
  aiApiKeyConfigured?: boolean;
  /** Custom Azure OpenAI endpoint if set. */
  aiEndpointOverride?: string;
  enableAccela: boolean;
  enableShopify: boolean;
  // Shopify skill packages
  enableShopifyCore?: boolean;
  enableShopifyCommerce?: boolean;
  enableShopifyBtoBOps?: boolean;
  enableShopifyLogistics?: boolean;
  enableWooCommerce?: boolean;
  // WooCommerce skill packages
  enableWooCommerceCore?: boolean;
  enableWooCommerceCommerce?: boolean;
  enableWooCommerceBtoBOps?: boolean;
  enableWooCommerceLogistics?: boolean;
  enableWooCommerceWrite?: boolean;
  enableMultipleDeployments: boolean;
  maxDeployments?: number;
  enableTicketing?: boolean;
  enableCoBrowse?: boolean;
  enableClickToCall?: boolean;
  maxIndexCount: number;
  // Reports
  enableConversationReports?: boolean;
  enableMissedChatReports?: boolean;
  enableSentimentReports?: boolean;
  enableAgentPerformanceReports?: boolean;
  enableResponseTimeReports?: boolean;
  enableCallReports?: boolean;
  enableAccountUtilizationReports?: boolean;
  enableAgentAvailabilityReports?: boolean;
  enableAgentUtilizationReports?: boolean;
  enableAuditReporting?: boolean;
  enableIvrReports?: boolean;
  enableCustomThresholds?: boolean;
  enableAgentDashboard?: boolean;
  enableScheduleAdherenceReports?: boolean;
  enableCustomDashboards?: boolean;
  maxCustomDashboards?: number;
  enableReportEmailSubscriptions?: boolean;
  enableTicketReports?: boolean;
  // Report Limits
  maxExportDays?: number;
  asyncResponseThresholdMinutes?: number | null;
  maxReportExportsPerMonth?: number;
  maxReportEmailsPerMonth?: number;
  maxScheduledReports?: number;
  // Channel Limits
  maxSmsPerMonth?: number | null;
  maxIvrMinutesPerMonth?: number | null;
  // Security
  enableEnterpriseSso?: boolean;
  enablePasskeys?: boolean;
  enableAdvancedMfa?: boolean;
  maxSsoProviders?: number;
  enableIpBlocking?: boolean;
  maxBlockedIps?: number;
  // Custom Statuses
  enableCustomStatuses?: boolean;
  maxCustomStatuses?: number;
  // Message Scrubbing
  enableMessageScrubbing?: boolean;
  // Agent Idle Detection
  enableAutoIdle?: boolean;
  autoIdleMinutes?: number;
  autoIdleStatus?: string;
  allowAgentIdleOverride?: boolean;
  // Usage & Limits
  maxLicensedUsers?: number;
  maxCreatedUsers?: number;
  licenseCooldownHours?: number;
  inboxRetentionDays?: number;
  activityLogRetentionDays?: number;
  maxConversationsPerMonth?: number;
  maxWorkflowTriggersPerMonth?: number;
  // Abuse-vector caps (mirror Subscription.cs — see copilot-kb/abuse-vector-limits.md)
  maxWebhookInvocationsPerMonth?: number;
  maxHttpRequestActionsPerMonth?: number;
  maxEmailCampaignSendsPerMonth?: number;
  maxWorkflowEvaluationsPerMonth?: number;
  maxBlobUploadBytesPerMonth?: number;
  maxCustomFields?: number;
  maxContacts?: number;
  maxOutboundEmailsPerMonth?: number;
  enableUsageAlerts?: boolean;
  usageWarningThreshold?: number;
  usageCriticalThreshold?: number;
  overageBehavior?: string;
  // SMS Alerts
  enableSmsAlerts?: boolean;
  maxSmsAlertsPerMonth?: number;
  // Threat Detection
  enableThreatDetection?: boolean;
  maxThreatAlertsPerMonth?: number;
  // Routing
  enableAutoRouting?: boolean;
  // Conversation Shadowing
  enableConversationShadowing?: boolean;
  // Video Chat
  enableVideoChat?: boolean;
  // ElevenLabs Bot Voice — TTS for AI chatbot replies
  enableBotVoice?: boolean;
  // Monthly character quota for bot chat TTS (0 = unlimited). Separate from IVR TTS.
  maxAvatarVoiceCharsPerMonth?: number;
  // Tier Escalation — structured Tier 1/2/3 routing per team with ring timeouts
  enableTierEscalation?: boolean;
  // Integrations — subscription-gated (mirrors Subscription.cs Enable* bool properties)
  // Add new integration flags here AND in integrationGateMap in IntegrationsLanding.tsx.
  // Run scripts/sync-subscription-flags.js to check sync with Subscription.cs.
  enableHubSpot?: boolean;
  enableNotion?: boolean;
  enableBigCommerce?: boolean;
  // BigCommerce skill packages
  enableBigCommerceCore?: boolean;
  enableBigCommerceCommerce?: boolean;
  enableBigCommerceBtoBOps?: boolean;
  enableBigCommerceLogistics?: boolean;
  enableMondaySkills?: boolean;
  enableDynamics365?: boolean;
  // Dynamics 365 skill packages
  enableDynamics365Sales?: boolean;
  enableDynamics365Service?: boolean;
  enableDynamics365Commerce?: boolean;
  enableDynamics365FieldService?: boolean;
  enableMagento?: boolean;
  // Magento skill packages
  enableMagentoCore?: boolean;
  enableMagentoCommerce?: boolean;
  enableMagentoBtoBOps?: boolean;
  enableMagentoLogistics?: boolean;
  enableSalesforce?: boolean;
  enableSalesforceSales?: boolean;
  // Salesforce skill packages
  enableSalesforceService?: boolean;
  enableSalesforceCommerce?: boolean;
  enableSalesforceFieldService?: boolean;
  enableKlaviyo?: boolean;
  enableServiceNow?: boolean;
  enableSapSkills?: boolean;
  enableSquare?: boolean;
  enableSquareRetail?: boolean;
  enableZapier?: boolean;
  enablePowerAutomate?: boolean;
  enableMake?: boolean;
  enableInChatPayments?: boolean;
  enableNetSuiteSkills?: boolean;
  enableHubSpotSkills?: boolean;
  enableHubSpotSales?: boolean;
  enablePipedriveSkills?: boolean;
  enablePipedriveSales?: boolean;
  enableQuickBooksSkills?: boolean;
  enableZohoSkills?: boolean;
  enableZohoSales?: boolean;
  enableToastSkills?: boolean;
  enableCloverSkills?: boolean;
  enableCloverCustomers?: boolean;
  enableCloverSales?: boolean;
  enableCloverPayments?: boolean;
  enableCloverInventory?: boolean;
  enableCloverEmployees?: boolean;
  enablePaymentsSkills?: boolean;
  enableMindbodySkills?: boolean;
  enableJobberSkills?: boolean;
  enableGuestySkills?: boolean;
  enableUplistingSkills?: boolean;
  enableCloudbedsSkills?: boolean;
  enableOpenTableSkills?: boolean;
  enableResySkills?: boolean;
  enableCalendlySkills?: boolean;
  enableOutlookCalendarSkills?: boolean;
  enableAcuitySchedulingSkills?: boolean;
  enableServiceTitan?: boolean;
  enableServiceTitanSkills?: boolean;
  enableServiceTitanService?: boolean;
  enableAirtable?: boolean;
  enableAirtableSkills?: boolean;
  enableAirtableSales?: boolean;
  enableNativeCrm?: boolean;
  enableCrmCustomFields?: boolean;
  enableDealHealthScore?: boolean;
  enableMcp?: boolean;
  enableMcpApi?: boolean;
  enableCopilotWrite?: boolean;
  enableCopilotStudio?: boolean;
  enableOrchestration?: boolean;
  // Channels
  enableAppleMessages?: boolean;
  // Ticketing add-ons
  enableTicketSla?: boolean;
  enableTicketAutomation?: boolean;
  enableTicketCsat?: boolean;
  enableTicketCoaching?: boolean;
  enableTicketMerge?: boolean;
  // Outbound Campaigns
  enableOutboundIvr?: boolean;
  enableSmartCallback?: boolean;
  // Call Center Analytics
  enableAdvancedCallCenterReports?: boolean;
  // Email
  enableEmailCampaigns?: boolean;
  enableGmailIntegration?: boolean;
  enableEmailAiResolution?: boolean;
  // Agent features
  enableCollisionDetection?: boolean;
  enableContactTimeline?: boolean;
  enableConversionGoals?: boolean;
  conversionEventsMonthlyLimit?: number;
  enableAgentSignatures?: boolean;
  enableAgentLeaderboard?: boolean;
  enableAgentBotObservation?: boolean;
  enableChatTranslations?: boolean;
  enableAgentToAgent?: boolean;
  enableAgentAssist?: boolean;
  // Workflow / AI
  enableWorkflowAi?: boolean;
  enableCommerceSkills?: boolean;
  enableOpsSkills?: boolean;
  enableProductCatalog?: boolean;
  enableQuoteGeneration?: boolean;
  enableAppointmentScheduling?: boolean;
  enableAppointmentReminders?: boolean;
  // Velaro Appointments product (master switch — text-to-book + multi-calendar + AI Q&A)
  enableAppointments?: boolean;
  enableConversationTraining?: boolean;
  enablePostChatCrmSummary?: boolean;
  enableQuoteMaker?: boolean;
  // Recruiting Bot
  enableRecruitingBot?: boolean;
  enableRecruitingBotEeoc?: boolean;
  // CRM / contacts
  enableCustomerHealthScore?: boolean;
  enableHealthScoreReports?: boolean;
  enableBuyingSignals?: boolean;
  // KB / Indexing — numeric limits read from DB, never hardcoded in UI
  enableScraperBotIndexing?: boolean;
  enableYoutubeBotIndexing?: boolean;
  enableBigCommerceBotIndexing?: boolean;
  enableShopifyBotIndexing?: boolean;
  enableScraperAutoRebuild?: boolean;
  scraperRebuildIntervalDays?: number;
  maxProductsIndexed?: number;       // products stored in AI index; never treat 0 as unlimited
  productSearchMode?: string;        // "keyword" | "hybrid" | "semantic" — explicit, not derived from count
  maxScraperPagesPerMonth?: number;
  maxUploadedPagesPerMonth?: number;
  ocrEnabled?: boolean;
  maxViewerSeats?: number;
  viewerSeatPricePerMonth?: number;
  /** "bot" | "chat" | "complete" | null (null = legacy, treat as "complete") */
  productTrack?: string;
  /** When false, human agents cannot log in — bot-only SKU. Default true. */
  enableLiveChat?: boolean;
  /** When false, AI bot is disabled — chat-only SKU. Default true. */
  enableBot?: boolean;
  maxKbArticles?: number;
  maxFileLibraryFiles?: number;
  kbAutoReindexIntervalHours?: number;
  enableKbBotIndexing?: boolean;
  enableFileIngestion?: boolean;
  enableOneDriveIngestionSync?: boolean;
  // Operations
  enableSoftphone?: boolean;
  enablePortalCustomDomain?: boolean;
  enableAvatarMode?: boolean;
  enableAvatarOnAgentTransfer?: boolean;
  enableWorkforceManagement?: boolean;
  enableOpsIntelligence?: boolean;
  enableSurveyReports?: boolean;
  enableServiceLevelReports?: boolean;
  enableSelfServeSeats?: boolean;
  enablePriorityQueue?: boolean;
  enablePokerRoomIvr?: boolean;
  enableIvrBridgeCall?: boolean;
  enableIvrTakeMessage?: boolean;
  // Per-site Deepgram emergency overrides (Velaro staff knob — flip a single site
  // to a different STT model or partials behavior without a deploy).
  deepgramModelOverride?: string;
  deepgramFilterPartialsOverride?: boolean;
  // Compliance — BYOS transcript archiving to Azure Blob / AWS S3
  enableTranscriptStorage?: boolean;
  enableTranscriptKeywordSearch?: boolean;
  // Multi-region data residency (CA / UK / EU). Default false = US cluster only.
  enableDataResidency?: boolean;
  // Multi-region inbox — separate inbox instances per region. Default false = global inbox only.
  enableMultiRegionInbox?: boolean;
  // Workflow UX redesign — V2 landing, amber rule node, run mode, global policies.
  enableNewWorkflowsUX?: boolean;
  // Rules panel visibility — default true for existing customers, false for new signups.
  // Runtime behavior (rule firing) is independent of this flag.
  showRulesPanel?: boolean;
  // Data Retention & GDPR
  enableDataRetention?: boolean;
  enableScheduledPurge?: boolean;
  minPurgeIntervalDays?: number;
  // Agent Login Security
  enableAgentIpRestriction?: boolean;
  enableSessionTimeout?: boolean;
  sessionTimeoutMinutes?: number;
  enableConcurrentSessionLimit?: boolean;
  maxConcurrentSessions?: number;
  // REST API Access
  maxApiKeys?: number;
  apiRateLimitPerMinute?: number;
  apiDailyLimit?: number;
  // Click-to-Call
  enableClickToCallWidget?: boolean;
  enableByokTwilioCtc?: boolean;
  enableCtcAiPrescreen?: boolean;
  enableCtcSmartScheduling?: boolean;
  enableCtcCallRecording?: boolean;
  enableCtcGeographicRouting?: boolean;
  enableClickToCallReports?: boolean;
  clickToCallMinutesPerMonth?: number;
  // Conversation Quality & Agent Intelligence
  enableConversationQuality?: boolean;
  enableAgentSmartReply?: boolean;
  enableChatIntelligenceReport?: boolean;
  // Bot Communications (IVR/bot outbound SMS and email)
  enableBotSmsSkill?: boolean;
  enableBotEmailSkill?: boolean;
  maxBotSmsPerHour?: number;
  maxBotEmailsPerHour?: number;
  enableAiComposedEmails?: boolean;
  maxAiComposedEmailsPerDay?: number;
  // Post Conversation compliance features
  enableComplianceMode?: boolean;
  enablePiiRedaction?: boolean;
  // RCS (Rich Communication Services)
  enableRcs?: boolean;
  enableRcsRichMessages?: boolean;
  rcsSmsFallbackEnabled?: boolean;
  maxRcsPerMonth?: number;
  // AI Skill Tier Gating
  enableAdvancedAiSkills?: boolean;    // Write-tier: create/update records, assign tasks, send emails
  enableDestructiveAiSkills?: boolean; // Destructive-tier: cancel orders, issue refunds, delete records
  // Visitor Tracking
  enableVisitorTracking?: boolean;
  visitorMonitorMaxVisible?: number; // plan cap (Starter=25, Pro=100, Enterprise=500)
  // Microsoft Bookings AI skills (requires M365 connection)
  enableMicrosoftBookingsSkills?: boolean;
  // VAST Retail (Kerridge CS) tire shop DMS AI skills
  enableVastSkills?: boolean;
  // Nextiva VoIP CTI — screen pop, call logging
  enableNextiva?: boolean;
  // AI model selection — lets customers pick their own model in bot settings.
  // Enterprise only by default; enabling on lower tiers risks unexpected cost increases.
  enableCustomAiModel?: boolean;
  // Theme & Intelligence
  enableThemeClassification?: boolean;
  enableConversationLifecycle?: boolean;
  enableIntelligenceSuite?: boolean;
  enableReportNarratives?: boolean;
  // E-commerce sync
  enableEcommerceSync?: boolean;
  // Operational alert rules
  enableOperationalAlerts?: boolean;
  // Integrations
  enableOutlookIntegration?: boolean;
  // IVR diagnostics panel
  enableIvrDiagnostics?: boolean;
  // Branding — false = white-label plan (can remove "Powered by Velaro" from widget + emails)
  showBranding?: boolean;
  // KB import
  enableJsonImport?: boolean;
  // BYOK AI — bring your own Anthropic/OpenAI key
  enableByokAi?: boolean;
  // AI Survey Builder — generate a complete survey draft from a goal description.
  enableAiSurveyBuilder?: boolean;
  // Mobile app — native iOS/Android app access for agents
  enableMobileApp?: boolean;
  // Mobile visitor alerts — push notifications for new visitor conversations
  enableMobileVisitorAlerts?: boolean;
  // Conversation session limits — bot abuse protection and cost controls
  maxTurnsPerConversation?: number;       // 0 = unlimited
  turnLimitWarnPercent?: number;          // 0 = no warning; 80 = warn at 80% of limit
  botLimitAction?: string;                // "close" | "escalate"
  botLimitMessage?: string;
  maxVisitorConversationsPerHour?: number; // 0 = unlimited
  // ── Synced from velaro-messaging Subscription.cs (source of truth) ──────
  maxShopifyConnections?: number;
  agentCanBlockContacts?: boolean;
  blockContactDisclaimerText?: string;
  maxMobilePocketMinutes?: number;
  licenseOverLimitNotifiedAt?: string;
  licenseEnforcementOverride?: boolean;
  industryVertical?: string;
  customThemeTaxonomy?: string;
  usageAlertEmails?: string;
  opsQueueDepthWarning?: number;
  opsQueueDepthCritical?: number;
  opsWaitWarningMinutes?: number;
  opsWaitCriticalMinutes?: number;
  opsUtilizationWarning?: number;
  opsUtilizationCritical?: number;
  agentThreadRetentionDays?: number;
  agentThreadComplianceRetentionDays?: number;
  maxOpenTickets?: number;
  maxTicketEmailRoutes?: number;
  enableAiAutoResolution?: boolean;
  maxEmailMailboxes?: number;
  maxTicketAutomationRules?: number;
  maxSlaPolicies?: number;
  maxTicketIntakeForms?: number;
  maxMcpSkillCallsPerMonth?: number;
  maxWorkflowAiCallsPerMonth?: number;
  maxAiToolCallsPerMonth?: number;
  maxSentimentAnalysesPerMonth?: number;
  maxAiSkillCallsPerMinute?: number;
  maxAiResolutionsPerMonth?: number;
  sentimentAiModel?: string;
  reviewCampaignEnabled?: boolean;
  reviewTargetPlatform?: string;
  reviewTargetUrl?: string;
  reviewIncentiveEnabled?: boolean;
  reviewIncentiveValueUsd?: number;
  reviewRequestDelayDays?: number;
  reviewRequestCooldownDays?: number;
  maxScraperJobsPerMonth?: number;
  maxIndexedChunkTotal?: number;
  maxAppointmentsPerMonth?: number;
  slaThresholdSeconds?: number;
  enableBotContainmentReport?: boolean;
  enableEscalationFunnelReport?: boolean;
  marketplaceSubscriptionId?: string;
  marketplacePlanId?: string;
  marketplaceStatus?: string;
  marketplaceQuantity?: number;
  marketplacePurchaserEmail?: string;
  maxLicensedSeats?: number;
  seatReassignmentCooldownDays?: number;
  additionalSeatPricePerMonth?: number;
  maxAutoResolvesPerMonth?: number;
  maxConcurrentConversations?: number;
  maxChatsPerAgent?: number;
  isVelaroInternalSite?: boolean;
  billingSource?: string;
  trialExpiresAt?: string;
  isTrial?: boolean;
  trialDaysCarriedOver?: number;
  hasDemoData?: boolean;
  hasPendingActivationNotice?: boolean;
  maxCustomEmailDomains?: number;
  softphoneIncludedMinutesPerMonth?: number;
  softphoneMinuteBlockSize?: number;
  softphoneMinuteBlockPrice?: number;
  packageId?: number;
  packageVersionId?: number;
  autoUpgradePackageVersion?: boolean;
  maxWorkflowRunsPerMonth?: number;
  autoBumpOnOverage?: boolean;
  billingPreference?: string;
  invoiceTermsAgreed?: boolean;
  invoiceTermsAgreedAt?: string;
  billingMethodType?: string;
  overageBehaviorOverride?: string;
  invoiceOverageBehaviorOverride?: string;
  autoUpgradeEnabled?: boolean;
  autoUpgradeNextTierId?: number;
  autoUpgradeConsentedAt?: string;
  autoUpgradeConsentedBy?: string;
  billingInterval?: string;
  currentTermStart?: string;
  currentTermEnd?: string;
  maxActiveWorkflows?: number;
  maxConcurrentWorkflowRuns?: number;
  enableScheduledWorkflows?: boolean;
  contactRetentionDays?: number;
  maxMonthlyAiTokens?: number;
  maxKnowledgeDocuments?: number;
  maxKnowledgeOverrides?: number;
  maxWebhookEndpoints?: number;
  maxPagesPerUploadedFile?: number;
  metricsRetentionDays?: number;
  lastKbReindexAt?: string;
  nextKbReindexAt?: string;
  lastKbArticlesChanged?: number;
  lastKbArticlesChecked?: number;
  conversationTrainingMonthlyLimit?: number;
  maxDeals?: number;
  maxPipelines?: number;
  maxCompanies?: number;
  healthScoreAlertEmails?: string;
  healthScoreAlertThreshold?: number;
  enableConversationSummary?: boolean;
  enableFocusInbox?: boolean;
  enableFocusVolumeReduction?: boolean;
  maxFocusRules?: number;
  // Visitor Insights caps (bool flags auto-injected in the #sync section below)
  maxVisitorAlertRules?: number;
  maxTrackedVisitors?: number;
  agentAssistSuggestionThreshold?: number;
  agentAssistAutoSendThreshold?: number;
  agentAssistAutoSendConsecutiveRequired?: number;
  agentAssistAutoSendMinConversations?: number;
  agentAssistStreakDecayDays?: number;
  agentAssistCsatHalfLifeDays?: number;
  ctcMaxRequestsPerCallerPerHour?: number;
  ctcMaxRequestsPerSitePerHour?: number;
  aiApiKeyOverride?: string;
  multilingualLanguageLimit?: number;
  billingSubscriptionId?: number;
  billingStatus?: string;
  billingEnforcedAt?: string;
  maxFileIngestionsPerMonth?: number;
  maxSavedQuotes?: number;
  maxIntelligenceReportsPerMonth?: number;
  maxReportNarrativesPerMonth?: number;
  maxAiCostPerMonthUsd?: number;
  maxMcpResponseTokensPerCall?: number;
  maxAiConversationsPerMonth?: number;
  aiConversationsUsedThisMonth?: number;
  aiConversationBlocksBalance?: number;
  aiConversationBlockSize?: number;
  aiConversationBlockPriceCents?: number;
  hardStopOnQuotaExhausted?: boolean;
  aiCostThisMonthCents?: number;
  aiCostAllTimeCents?: number;
  aiCostAlertThresholdCents?: number;
  aiBlockPriceHaikuCents?: number;
  aiBlockPriceSonnetCents?: number;
  customerAiEndpoint?: string;
  customerAiKeyEncrypted?: string;
  enableQuickStartWizards?: boolean;
  enablePageWidgets?: boolean;
  enableSecureForms?: boolean;
  enableComplianceStarter?: boolean;
  enableCompliancePro?: boolean;
  enableComplianceEnterprise?: boolean;
  enableOneDriveAttachments?: boolean;
  enableTeamsNotifications?: boolean;
  enableSlackNotifications?: boolean;
  enableCalendarBooking?: boolean;
  enableApiAccess?: boolean;
  enableIvrTriage?: boolean;
  enableIvrAvailabilityCheck?: boolean;
  enableIvrSmartQueue?: boolean;
  enableIvrAcd?: boolean;
  enableMultilingualIVR?: boolean;
  enableStripeBilling?: boolean;
  enableProactiveEngagement?: boolean;
  enableLandingPages?: boolean;
  enableAdvancedCampaignReports?: boolean;
  enableJourneyWorkflows?: boolean;
  journeyTtlDays?: number;
  // Stage 2c.7 — per-customer load gates (plan Section 12)
  enableJourneyHighVolumeEvents?: boolean;
  maxConcurrentJourneyWaits?: number;
  maxJourneyEventsPerMinute?: number;
  enableAgentBusyMessages?: boolean;
  enableCollectionsCompliance?: boolean;
  enableRegFOptOutEnforcement?: boolean;
  enableDisputeTracking?: boolean;
  enableCfpbExport?: boolean;
  enableConsumerSelfServicePortal?: boolean;
  enableAiTriage?: boolean;
  enableEveryVisitorAlert?: boolean;
  enableVelaroInternalSkills?: boolean;
  enableFocusAi?: boolean;
  enablePremiumAiModel?: boolean;
  enableVisitorInsights?: boolean;
  enableVisitorInsightsAdvanced?: boolean;
  // KB Import
  enableKbImport?: boolean;
  maxKbImportArticlesPerMonth?: number;
  enableAdvancedRouting?: boolean;
  enableVoiceToText?: boolean;
    enableMobileRichWidgets?: boolean;
    enableCustomerIntelligenceSnapshot?: boolean;
    enableChatToEmailResume?: boolean;
    enableTwitter?: boolean;
  // #sync-end — auto-generated flags are injected above this line by sync-subscription-flags.js --fix
}

export function SubscriptionsProvider(props: { children: React.ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  const lastFetchRef = useRef(0);

  const fetch = useCallback(async () => {
    lastFetchRef.current = Date.now();
    try {
      const response = await api.messaging.get("Subscription");
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch {
      // keep subscription null — consumers use safe defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Re-fetch on window/tab focus so subscription flags stay fresh after a
  // package-apply or flag change without requiring a hard reload.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastFetchRef.current >= FOCUS_REFETCH_DEBOUNCE_MS) {
        fetch();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [fetch]);

  return (
    <SubscriptionsContext.Provider value={{ subscription, loading }}>
      {props.children}
    </SubscriptionsContext.Provider>
  );
}
